import { DRINK_STATES } from "../combat/anim.ts";
import { fieldsOf, isPlainRecord, oneOf, report, whole, type Place } from "./checks.ts";
import type { AbilityShape, Drink, DrinkVariant } from "./types.ts";

/**
 * An ability's `drink` (ADR-074's amendment of 2026-10-27, ADR-134 decision 7): what marks a potion's record, so the
 * map server writes the variant's animation state for every watcher and the page draws the burst in the variant's tone.
 */

/**
 * The variants a drink may name, one animation state each (`DRINK_STATES`).
 */
export const DRINK_VARIANTS: readonly DrinkVariant[] = [...DRINK_STATES.keys()] as DrinkVariant[];

/**
 * The least and the most a drink's `percent` may be: the burst's size for the drinker's own page, as a whole percent of
 * the definition's (60 for a minor potion, 100 for a standard one, above 100 for a stronger one).
 */
export const MIN_DRINK_PERCENT = 25;
export const MAX_DRINK_PERCENT = 200;

/**
 * Checks an ability's `drink`: null for none, or a variant and a percent, which only a record aimed at oneself that plays the `heal` strip may carry.
 *
 * @param place Where to report.
 * @param value The drink.
 * @param shape The ability's shape, null when it was wrong.
 * @param strip The ability's strip, null when it was wrong.
 * @returns The drink, null for none, or undefined when it is missing or wrong.
 */
export function drinkOf(place: Place, value: unknown, shape: AbilityShape | null, strip: string | null): Drink | null | undefined {
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  if (!isPlainRecord(value)) {
    report(place, "drink is neither null nor a record");
    return undefined;
  }
  const fields = fieldsOf(place, value, "drink", ["variant", "percent"], "a drink") as Readonly<Record<string, unknown>>;
  const variant = oneOf(place, fields["variant"], "drink.variant", DRINK_VARIANTS, "a drink variant");
  const percent = whole(place, fields["percent"], "drink.percent", MIN_DRINK_PERCENT, MAX_DRINK_PERCENT);
  if (shape !== null && shape.kind !== "self") {
    report(place, `a drink lands on oneself, and this shape is a ${shape.kind}`);
    return undefined;
  }
  if (strip !== null && strip !== "heal") {
    report(place, `a drink plays the heal strip, not ${strip}`);
    return undefined;
  }
  return variant === null || percent === null || Object.keys(fields).length !== 2 ? undefined : { variant, percent };
}
